import {
  Component,
  EventEmitter,
  forwardRef,
  input,
  Input,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  ControlValueAccessor,
  UntypedFormControl,
  UntypedFormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms'
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { MatIconModule } from '@angular/material/icon'

import { DropdownOption } from '@wm-front/core'

@UntilDestroy()
@Component({
  selector: 'wm-front-input-dropdown',
  templateUrl: './input-dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => InputDropdownComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      useExisting: forwardRef(() => InputDropdownComponent),
      multi: true,
    },
  ],
  styleUrls: ['./input-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [NgSelectModule, ReactiveFormsModule, MatIconModule],
})
export class InputDropdownComponent
  implements OnInit, Validator, ControlValueAccessor
{
  /** The input's label */
  @Input() public label?: string

  /** The icon that can be shown at the right of the input */
  @Input() public icon: string

  /** The input's placeholder */
  @Input() public placeholder: string

  /** Whether the input is dsabled */
  @Input() public disabled: boolean

  /** The input's not found search result */
  @Input() public notFoundText: string

  /** The name of the input */
  @Input() public name: string

  /** Event that triggers when a value is picked in the dropdown */
  @Output() public optionSelected: EventEmitter<DropdownOption | undefined> =
    new EventEmitter()

  /** Refs to the ngSelect component instance */
  @ViewChild('ngSelect') public ngSelect: NgSelectComponent

  public appendTo = input<string>('')

  public isOptional = input(false)

  /** Whether or not the dropdown is opened */
  public dropdownOpened = false

  /** The forms that hold the field required who contains nested input */
  public formGroup: UntypedFormGroup

  /** The options to display in the dropdown options */
  private _options: DropdownOption[] = []

  /** The current value */
  private value: number | string

  /** The current selected option */
  public get selectedOption(): DropdownOption | undefined {
    return this._options.find((option) => option.checked)
  }

  /** Get the dropdown options */
  public get options(): DropdownOption[] {
    return this._options
  }

  /** Set the dropdown options */
  @Input()
  public set options(opts: DropdownOption[]) {
    this._options = opts
    if (this.value) {
      this.writeValue(this.value)
    }
  }

  public ngOnInit(): void {
    this.formGroup = new UntypedFormGroup({
      select: new UntypedFormControl(
        undefined,
        this.isOptional() ? undefined : Validators.required
      ),
    })

    this.formGroup.controls['select'].valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((v) => {
        this.onChange && this.onChange(this.transform(v))
        this.checkedOption(v)
      })

    const checkedOption = this._options?.find((option) => option.checked)

    if (checkedOption) {
      this.formGroup.controls['select'].setValue(checkedOption.value)
    }
  }

  /** Reset the dropdown */
  public resetSelect(): void {
    if (this.ngSelect.hasValue) {
      this.ngSelect.clearModel()
    }
  }

  /** Toggles the dropdown */
  public toggleDropdown(): void {
    if (!this.dropdownOpened) {
      this.ngSelect.open()
      this.dropdownOpened = true
    } else {
      this.dropdownOpened = false
    }
  }

  /** Opens the dropdown */
  public openDropdown(): void {
    this.dropdownOpened = true
  }

  /** Closes the dropdown */
  public closeDropdown(): void {
    setTimeout(() => {
      this.dropdownOpened = false
    })
  }

  /** Handle blur event */
  public onBlur(): void {
    this.onTouched()
  }

  /** Updates the form value when the selectedOption is changed */
  public selectOption(option: DropdownOption | undefined): void {
    if (option) {
      this.formGroup.controls['select'].setValue(option.value)
      this.checkedOption(option.value)
      this.optionSelected.emit(this.selectedOption)
    }
  }

  /** Init value function given by the implementation of controlValueAccessor */
  public writeValue(newValue: unknown): void {
    if (newValue !== undefined) {
      this.value = newValue as string
      const option = this.options?.find(({ value }) => value === newValue)

      if (option) {
        option.checked = true
        this.formGroup.controls['select'].setValue(option.value)
      }
    }
  }

  /** Handle pristine behaviour */
  public registerOnChange(fn: any): void {
    this.onChange = fn
  }

  /** Handle blur behaviour */
  public registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  /** Handle enable forms state */
  public setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.formGroup.disable() : this.formGroup.enable()
  }

  /** Handle form validation */
  public validate(): ValidationErrors | null {
    return this.formGroup.valid
      ? null
      : {
          invalidForm: {
            valid: false,
            message: 'formGroup fields are invalid',
          },
        }
  }

  /** Method which will state if one option is equal to another */
  public compareWith(option1: DropdownOption, option2: string): boolean {
    return option1.value === option2
  }

  /** Handle value return by the form */
  private transform(val: string): string {
    return val
  }

  /** Reset the options list and add checked to the good one */
  private checkedOption(value: string | number | boolean): void {
    this._options?.forEach((item) => {
      item.checked = false
    })

    const newCheckedOptionIndex = this._options?.findIndex((item) => {
      return item.value === value
    })

    if (newCheckedOptionIndex >= 0) {
      this._options[newCheckedOptionIndex].checked = true
    }
  }

  /** Handle pristine behaviour */
  private onChange: (name: string) => void = () => undefined

  /** Handle touch behaviour */
  private onTouched: () => void = () => undefined
}
